import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { DataSource } from "../api/types";
import { useToast, errText, JsonArea, DirtyChip, EnabledChip } from "../components/ui";

const KINDS = ["ccxt", "cryptofeed", "alchemy", "moomoo"];
const EVENT_HINT = "TRADE, QUOTE, ORDER_BOOK, KLINE, FUNDING_RATE, OPEN_INTEREST, LONG_SHORT_RATIO, LIQUIDATION";

const parseList = (s: string): string[] =>
  s.split(",").map((x) => x.trim()).filter(Boolean);
const joinList = (v: unknown): string => (Array.isArray(v) ? v.join(", ") : "");

export default function DataSources({ onChanged }: { onChanged: () => void }) {
  const notify = useToast();
  const [rows, setRows] = useState<DataSource[]>([]);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState("ccxt");

  const load = () => api.listDataSources().then(setRows).catch((e) => notify({ kind: "error", msg: errText(e) }));
  useEffect(() => { load(); }, []);

  const addNew = async () => {
    if (!newName.trim()) return;
    try {
      await api.upsertDataSource(newName.trim(), { kind: newKind, params: { symbols: [], event_types: ["TRADE"] }, enabled: true });
      setNewName("");
      await load();
      onChanged();
      notify({ kind: "ok", msg: `已新增数据源草稿: ${newName}` });
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };

  return (
    <div>
      <div className="panel">
        <div className="row">
          <div><label>新增数据源名称</label><input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="如 binance_spot" /></div>
          <div style={{ maxWidth: 160 }}><label>类型</label>
            <select value={newKind} onChange={(e) => setNewKind(e.target.value)}>{KINDS.map((k) => <option key={k}>{k}</option>)}</select>
          </div>
          <div style={{ flex: "0 0 auto" }}><button className="primary" onClick={addNew}>新增</button></div>
        </div>
      </div>

      {rows.map((ds) => (
        <SourceCard key={ds.name} ds={ds} onSaved={() => { load(); onChanged(); }} />
      ))}
      {rows.length === 0 && <div className="muted">暂无数据源</div>}
    </div>
  );
}

function SourceCard({ ds, onSaved }: { ds: DataSource; onSaved: () => void }) {
  const notify = useToast();
  const p = ds.params || {};
  const [enabled, setEnabled] = useState(ds.enabled);
  const [symbols, setSymbols] = useState(joinList(p.symbols));
  const [events, setEvents] = useState(joinList(p.event_types));
  const [other, setOther] = useState<Record<string, unknown>>(() => {
    const { symbols: _s, event_types: _e, ...rest } = p as Record<string, unknown>;
    return rest;
  });

  const save = async () => {
    const params = { ...other, symbols: parseList(symbols), event_types: parseList(events) };
    try {
      await api.upsertDataSource(ds.name, { kind: ds.kind, params, enabled });
      onSaved();
      notify({ kind: "ok", msg: `已保存草稿: ${ds.name}` });
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };
  const remove = async () => {
    if (!confirm(`删除数据源 ${ds.name}?`)) return;
    try { await api.deleteDataSource(ds.name); onSaved(); } catch (e) { notify({ kind: "error", msg: errText(e) }); }
  };

  return (
    <div className="card">
      <div className="card-head">
        <span className="title mono">{ds.name}</span>
        <span className="badge kind">{ds.kind}</span>
        <EnabledChip enabled={enabled} />
        <DirtyChip dirty={ds.dirty} />
        <span className="spacer" />
        <button onClick={() => setEnabled(!enabled)}>{enabled ? "停用" : "启用"}</button>
        <button onClick={save}>保存草稿</button>
        <button className="danger" onClick={remove}>删除</button>
      </div>
      <div className="grid2" style={{ marginTop: 10 }}>
        <div><label>标的 symbols（逗号分隔）</label><input value={symbols} onChange={(e) => setSymbols(e.target.value)} placeholder="BTC-USDT, ETH-USDT / US.TSLA" /></div>
        <div><label>订阅事件 event_types（逗号分隔）</label><input value={events} onChange={(e) => setEvents(e.target.value)} placeholder={EVENT_HINT} /></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label>其它连接参数（JSON，如 exchange/host/port/market_type）</label>
        <JsonArea value={other} onChange={setOther} rows={4} />
      </div>
    </div>
  );
}
